import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Mailbox } from '@mailpilot/shared';
import { api, type ResOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { isDesktop } from '../../lib/desktop';
import { useToast } from '../ui';

type OAuthProvider = 'gmail' | 'outlook';
type MailboxesRes = ResOf<'GET /api/mailboxes'>;

const POLL_MS = 3000;
const POLL_MAX_MS = 3 * 60_000;

/**
 * Starts an official OAuth flow. On the web the browser navigates to the
 * provider; on desktop the system browser opens and we poll for the result.
 */
export function useOAuthConnect() {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<OAuthProvider | null>(null);
  const [polling, setPolling] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const poll = useCallback(
    (before: Mailbox[], reconnectMailboxId?: string) => {
      stop();
      setPolling(true);
      const known = new Set(before.map((m) => m.id));
      const started = Date.now();
      timer.current = setInterval(async () => {
        if (Date.now() - started > POLL_MAX_MS) {
          stop();
          toast.info('Stopped waiting for the browser sign-in. If you finished it, refresh this page.');
          return;
        }
        try {
          const res = await qc.fetchQuery({ queryKey: qk.mailboxes, queryFn: () => api('GET /api/mailboxes'), staleTime: 0 });
          if (reconnectMailboxId) {
            const mb = res.mailboxes.find((m) => m.id === reconnectMailboxId);
            if (mb && mb.status !== 'needs_reauth') {
              stop();
              toast.success(`${mb.email} reconnected`);
            }
            return;
          }
          const fresh = res.mailboxes.find((m) => !known.has(m.id));
          if (fresh) {
            stop();
            toast.success(`${fresh.email} connected`);
          }
        } catch {
          /* keep polling; transient errors are fine */
        }
      }, POLL_MS);
    },
    [qc, stop, toast],
  );

  const start = useCallback(
    async (provider: OAuthProvider, reconnectMailboxId?: string) => {
      setPending(provider);
      try {
        const desktop = isDesktop();
        const { authUrl } = await api('POST /api/mailboxes/oauth/start', {
          body: { provider, reconnectMailboxId, returnTo: desktop ? 'desktop' : 'web' },
        });
        if (desktop) {
          const before = qc.getQueryData<MailboxesRes>(qk.mailboxes)?.mailboxes ?? [];
          window.open(authUrl, '_blank');
          toast.info('Finish signing in with your browser. MailPilot will pick up the connection automatically.');
          poll(before, reconnectMailboxId);
        } else {
          window.location.href = authUrl;
        }
      } catch (err) {
        toast.error(err);
      } finally {
        setPending(null);
      }
    },
    [poll, qc, toast],
  );

  return { start, pending, polling, stopPolling: stop };
}
